import { isScalar, type RuntimeSchema } from '@takeshape/schema';
import { pathToString } from '@takeshape/util';
import { getPreviewBranchRuntimeSchemaModel } from 'app/branches/selectors';
import type { ChangeFieldAction } from 'app/schema-form/actions';
import { initializeArray } from 'app/schema-form/actions';
import { fromJS } from 'immutable';
import type { SagaIterator } from 'redux-saga';
import { put, select } from 'redux-saga/effects';
import type { SetAgentsToolArgDefaultsCreator, SetAiToolArgDefaultsCreator } from './actions.js';

export function createToolArgsDefaultsSaga(
  formName: string,
  actionCreator: SetAiToolArgDefaultsCreator | SetAgentsToolArgDefaultsCreator,
  predicate: (action: ChangeFieldAction) => boolean
) {
  return function* toolArgsDefaultSaga(action: ChangeFieldAction): SagaIterator {
    if (typeof action.payload !== 'string' || action.meta.formName !== formName) {
      return;
    }

    if (!predicate(action)) {
      return;
    }

    const { payload, meta } = action;

    const { path } = meta;

    const runtimeSchema: RuntimeSchema | null = yield select(getPreviewBranchRuntimeSchemaModel);

    if (!runtimeSchema) {
      return;
    }

    const argsSchema = runtimeSchema.getTool(payload as string)?.parametersSchema;

    if (!argsSchema) {
      return;
    }

    const args: string[] = Object.keys(argsSchema.properties).filter((argName) => {
      if (argsSchema.required?.includes(argName)) {
        return true;
      }
      const arg = argsSchema.properties[argName];
      return typeof arg.type === 'string' && isScalar(arg.type);
    });

    const argsPath = [...path.slice(0, -1), 'args'];

    yield put(
      actionCreator(
        {
          args: fromJS(args)
        },
        {
          path: ['data', ...argsPath]
        }
      )
    );

    yield put(
      initializeArray(formName, pathToString(argsPath), {
        formName,
        path: argsPath,
        collapseByDefault: true,
        reinit: true
      })
    );
  };
}
